/**
 * 確認頁即時報價區塊：大字顯示幣別總價（displayPrice＋displayCurrency）、
 * 小字 HKD 權威價＋匯率快照、明細（基本價＋加購，HKD 計價明細可顯示）。
 * 查無價（priceHkd === null）→ 黃色警示，送出由呼叫端 disabled。
 */
import { AlertTriangle } from "lucide-react";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "../../../../../server/routers";
import { useLanguage } from "../../../contexts/LanguageContext";
import { fmtMoney } from "./wizard-shared";

export type EstimateResult = inferRouterOutputs<AppRouter>["order"]["estimate"];

export function PriceSummary({ estimate, isLoading }: { estimate: EstimateResult | undefined; isLoading: boolean }) {
  const { t } = useLanguage();

  if (isLoading || !estimate) {
    return <div className="h-28 animate-pulse rounded-lg bg-muted/40" />;
  }

  if (estimate.priceHkd === null) {
    return (
      <div className="flex items-start gap-3 rounded-md border border-[color-mix(in_oklab,var(--color-warning)_45%,transparent)] bg-[color-mix(in_oklab,var(--color-warning)_14%,transparent)] p-4">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-warning)]" />
        <div className="space-y-0.5">
          <p className="text-[13px] font-medium">
            {estimate.reason === "no_exchange_rate"
              ? t({ "zh-TW": "尚未設定顯示幣別匯率，暫無法線上計價", "en-US": "Exchange rate not configured — online pricing unavailable", "zh-CN": "尚未设定显示币别汇率，暂无法在线计价" })
              : t({ "zh-TW": "此路線尚未開放線上計價", "en-US": "This route is not yet available for online pricing", "zh-CN": "此路线尚未开放在线计价" })}
          </p>
          <p className="text-[12px] text-muted-foreground">
            {t({ "zh-TW": "請聯繫平台，由調度員協助處理。", "en-US": "Please contact the platform — our dispatcher will assist you.", "zh-CN": "请联系平台，由调度员协助处理。" })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-primary/40 bg-gradient-to-br from-primary-soft/50 to-card">
      <div className="flex flex-wrap items-end justify-between gap-3 p-4">
        <div>
          <p className="text-[12px] font-medium uppercase tracking-[0.14em] text-primary">
            {t({ "zh-TW": "預估總價", "en-US": "Estimated total", "zh-CN": "预估总价" })}
          </p>
          <p className="numeric font-display text-[32px] font-medium leading-tight tracking-tight">
            {fmtMoney(estimate.displayCurrency, estimate.displayPrice)}
          </p>
          {estimate.displayCurrency !== "HKD" && (
            <p className="text-[12px] text-muted-foreground">
              {t({
                "zh-TW": `以 HKD ${estimate.priceHkd.toLocaleString()} 計價，匯率 ${estimate.exchangeRate}`,
                "en-US": `Priced at HKD ${estimate.priceHkd.toLocaleString()}, rate ${estimate.exchangeRate}`,
                "zh-CN": `以 HKD ${estimate.priceHkd.toLocaleString()} 计价，汇率 ${estimate.exchangeRate}`,
              })}
            </p>
          )}
        </div>
      </div>
      <div className="space-y-1.5 border-t border-border/70 bg-card/60 p-4 text-[13px]">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">{t({ "zh-TW": "基本價", "en-US": "Base fare", "zh-CN": "基本价" })}</span>
          <span className="numeric">{fmtMoney("HKD", estimate.baseHkd)}</span>
        </div>
        {estimate.addons.map((a) => (
          <div key={a.code} className="flex items-center justify-between">
            <span className="text-muted-foreground">
              {t({ "zh-TW": "加購", "en-US": "Add-on", "zh-CN": "加购" })} · {a.name}
            </span>
            <span className="numeric">{fmtMoney("HKD", a.priceHkd)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between border-t border-border/60 pt-1.5 font-medium">
          <span>{t({ "zh-TW": "合計（HKD）", "en-US": "Total (HKD)", "zh-CN": "合计（HKD）" })}</span>
          <span className="numeric">{fmtMoney("HKD", estimate.priceHkd)}</span>
        </div>
      </div>
    </div>
  );
}
